"use client";

/**
 * Guards leaving a multi-step CTA step that has unsaved input: a step reports whether its form is
 * open (`setIsEditing`), any input while it is marks the step dirty (`markInput`), and a guarded
 * navigation (`guardLeave`) waits for an inline "Discard your changes?" answer instead of running.
 * Resets whenever the dialog shows a different view.
 */

import { useCallback, useEffect, useState } from "react";

interface UseUnsavedStepGuardReturn {
  /** Whether the open step reports its form as open for input. */
  isEditing: boolean;
  /** Whether leaving now would lose typed input. */
  isDirty: boolean;
  /** Whether a navigation is waiting on the discard question. */
  isAsking: boolean;
  /** Reported by the step: true while its form is open for input. */
  setIsEditing: (isEditing: boolean) => void;
  /** Call on any input inside the step. */
  markInput: () => void;
  /** Runs `leave` now, or after the guest confirms discarding their changes. */
  guardLeave: (leave: () => void) => void;
  /** Answer "Keep editing": drop the waiting navigation. */
  keepEditing: () => void;
  /** Answer "Discard": run the waiting navigation. */
  discard: () => void;
}

/**
 * Tracks unsaved input in the open step and gates navigation away from it.
 * @param viewKey - Identifies the dialog's current view; a new view starts clean
 * @returns The guard state and actions
 * @throws Never
 */
export function useUnsavedStepGuard(viewKey: string): UseUnsavedStepGuardReturn {
  // States
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedInput, setHasUnsavedInput] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);

  // Derived values
  const isDirty = isEditing && hasUnsavedInput;

  // Handlers
  const markInput = useCallback(() => {
    if (isEditing) setHasUnsavedInput(true);
  }, [isEditing]);

  const guardLeave = useCallback(
    (leave: () => void) => {
      if (isDirty) setPendingLeave(() => leave);
      else leave();
    },
    [isDirty]
  );

  const keepEditing = useCallback(() => setPendingLeave(null), []);

  const discard = useCallback(() => {
    const leave = pendingLeave;
    setPendingLeave(null);
    setHasUnsavedInput(false);
    leave?.();
  }, [pendingLeave]);

  // Effects
  // A new view starts clean: nothing typed yet, and no navigation waiting on the question.
  useEffect(() => {
    setHasUnsavedInput(false);
    setPendingLeave(null);
  }, [viewKey]);

  // Saving or cancelling an edit closes the form; what was typed is no longer at risk.
  useEffect(() => {
    if (!isEditing) setHasUnsavedInput(false);
  }, [isEditing]);

  return {
    isEditing,
    isDirty,
    isAsking: pendingLeave !== null,
    setIsEditing,
    markInput,
    guardLeave,
    keepEditing,
    discard,
  };
}
